// Generic failure reporter — pushes any failure worth knowing about straight
// to Google Chat instead of requiring someone to reproduce it or dig through
// browser devtools on a client's machine. Covers HTTP failures (with
// request/response) as well as failures that never talk to a backend at all
// (React render errors, uncaught exceptions) — those just carry a reason.
import { config } from "../config/config";

const CHAT_MESSAGE_LIMIT = 1900;

export type DiscordNotifyEvent = {
  // What broke, in a few words — shown as the message title.
  title: string;
  // Where/what this is about (page, resource, user action, etc).
  context?: Record<string, unknown>;
  // What we sent, if anything sent went out at all.
  request?: unknown;
  // What came back, if anything came back at all.
  response?: unknown;
  // Why it failed — a plain reason, not necessarily tied to a request.
  reason?: string;
};

const stringify = (value: unknown): string => {
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

// This is customer-facing data (order details, addresses, financial
// reports) — request/response bodies get redacted before ever leaving the
// app, so a chat alert can never leak PII or business-sensitive figures.
// Only structural fields (ids, statuses, endpoints, counts) pass through.
const SENSITIVE_KEYWORDS = [
  "name",
  "email",
  "phone",
  "address",
  "city",
  "county",
  "district",
  "municipality",
  "postal",
  "state",
  "region",
  "company",
  "buyer",
  "seller",
  "customer",
  "recipient",
  "financial",
  "cost",
  "price",
  "profit",
  "commission",
  "tax",
  "refund",
  "discount",
  "amount",
  "total",
  "warehouse",
  "shipping",
  "iban",
  "account",
  "card",
  "ssn",
  "tc",
  "token",
  "password",
  "secret",
];

const isSensitiveKey = (key: string): boolean => {
  const lowerKey = key.toLowerCase();
  return SENSITIVE_KEYWORDS.some((word) => lowerKey.includes(word));
};

const REDACTED = "[redacted]";
const MAX_REDACT_DEPTH = 6;

const redact = (value: unknown, depth = 0): unknown => {
  if (depth > MAX_REDACT_DEPTH) return REDACTED;

  if (Array.isArray(value)) {
    return value.map((item) => redact(item, depth + 1));
  }

  if (value && typeof value === "object") {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      if (isSensitiveKey(key)) {
        result[key] = Array.isArray(val)
          ? `[redacted array(${val.length})]`
          : REDACTED;
      } else {
        result[key] = redact(val, depth + 1);
      }
    }
    return result;
  }

  return value;
};

const STACK_TRACE_KEYS = ["stackTrace", "stack"];
const MAX_STACK_LINES = 4;

// Backend error bodies (and JS error objects) put a full stack trace right
// next to the fields that actually matter (message, requestId, endpoint) —
// a flat character-count truncation would just bury those under a wall of
// framework internals. Pulling the stack trace out, trimming it hard, and
// putting it back at the end keeps the useful part visible regardless of
// how long the trace is.
const formatSection = (value: unknown, maxLen: number): string => {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    const entries = { ...(value as Record<string, unknown>) };
    const stackKey = STACK_TRACE_KEYS.find(
      (key) => typeof entries[key] === "string",
    );
    if (stackKey) {
      const fullStack = entries[stackKey] as string;
      const lines = fullStack.split("\n");
      const trimmedStack =
        lines.slice(0, MAX_STACK_LINES).join("\n") +
        (lines.length > MAX_STACK_LINES ? "\n... (stack trace truncated)" : "");
      delete entries[stackKey];
      return stringify({ ...entries, [stackKey]: trimmedStack }).slice(
        0,
        maxLen,
      );
    }
  }
  return stringify(value).slice(0, maxLen);
};

const section = (label: string, value: unknown, maxLen = 800): string => {
  if (value === undefined || value === null || value === "") return "";
  return `*${label}:*\n\`\`\`\n${formatSection(value, maxLen)}\n\`\`\`\n`;
};

// Not imported directly (would create a circular import — authProvider.ts
// pulls in utils/axios.ts, which pulls in this file) — index.tsx registers
// this at startup instead, with sellerId, the selected marketplace, and the
// account holder's display name (same given_name/family_name already shown
// in the UI's own "welcome" header, so this isn't a new exposure).
type AccountContext = {
  sellerId?: string;
  marketplace?: string;
  account?: string;
  storeName?: string;
  env?: string;
  route?: string;
  userAgent?: string;
};

let getAccountContext: () => AccountContext = () => ({});
export const registerAccountContextProvider = (
  provider: () => AccountContext,
) => {
  getAccountContext = provider;
};

const sendToGoogleChat = async (content: string): Promise<void> => {
  if (!config.GOOGLE_CHAT_WEBHOOK_URL) return;
  try {
    await fetch(config.GOOGLE_CHAT_WEBHOOK_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json; charset=UTF-8" },
      body: JSON.stringify({ text: content }),
    });
  } catch (e) {
    console.error("[discordNotifier] failed to send to Google Chat:", e);
  }
};

export const notifyDiscord = async (
  event: DiscordNotifyEvent,
): Promise<void> => {
  const context = {
    ...getAccountContext(),
    ...(redact(event.context) as Record<string, unknown> | undefined),
  };

  const content = [
    `🔴 *${event.title}*`,
    section("Context", context),
    section("Request (ne gitti)", redact(event.request)),
    section("Response (ne döndü)", redact(event.response), 1200),
    section("Reason (ne oldu)", event.reason),
  ]
    .filter(Boolean)
    .join("\n")
    .slice(0, CHAT_MESSAGE_LIMIT);

  await sendToGoogleChat(content);
};

// Catches what ErrorBoundary can't: errors thrown outside React's render
// tree (event handlers, timers, non-React async code) and unhandled promise
// rejections. Call once at app startup.
export const setupGlobalErrorReporting = () => {
  window.addEventListener("error", (event) => {
    void notifyDiscord({
      title: "Frontend — uncaught error",
      context: {
        pageUrl: window.location.href,
        source: event.filename,
        line: event.lineno,
        column: event.colno,
      },
      reason: event.error?.stack ?? event.message,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    console.error("Unhandled promise rejection:", event.reason);
  });
};
